import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Upload, Download, X, FileText } from "lucide-react";

export const CsvImportButton = ({ label = "Subir CSV", modes, columnsHelp, templates = [], onImport, testId = "csv-import" }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState(modes?.[0]?.id);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const text = await file.text();
      const ok = await onImport(text, mode);
      if (ok !== false) setOpen(false);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadTemplate = (t) => {
    const blob = new Blob([t.content], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = t.filename;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}
        className="border-[#103B31] text-[#103B31] hover:bg-[#103B31]/5"
        data-testid={`${testId}-btn`}>
        <Upload className="w-3.5 h-3.5 mr-1" /> {label}
      </Button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid={`${testId}-modal`}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
              <h3 className="font-bold text-[#0F172A]">{label}</h3>
              <button onClick={() => setOpen(false)} data-testid={`${testId}-close`}>
                <X className="w-5 h-5 text-[#64748B]" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {modes && (
                <div className="grid grid-cols-2 gap-2">
                  {modes.map((m) => (
                    <button key={m.id} onClick={() => setMode(m.id)}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${mode === m.id ? "border-[#103B31] bg-[#103B31]/5" : "border-gray-200"}`}
                      data-testid={`${testId}-mode-${m.id}`}>
                      <p className="text-sm font-bold text-[#0F172A]">{m.label}</p>
                      <p className="text-[11px] text-[#64748B] mt-0.5">{m.desc}</p>
                    </button>
                  ))}
                </div>
              )}

              {columnsHelp && (
                <p className="text-xs text-[#64748B] bg-gray-50 rounded-lg p-3 leading-relaxed">{columnsHelp}</p>
              )}

              {templates.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8]">Paso 1 · Descarga la plantilla</p>
                  {templates.filter(t => !t.mode || t.mode === mode).map((t) => (
                    <button key={t.filename} onClick={() => downloadTemplate(t)}
                      className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-[#103B31] text-[#103B31] text-sm font-bold hover:bg-[#103B31]/5 transition-colors"
                      data-testid={`${testId}-template-${t.filename}`}>
                      <Download className="w-4 h-4" /> {t.label}
                    </button>
                  ))}
                </div>
              )}

              <p className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8]">Paso 2 · Llénala y súbela</p>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid={`${testId}-file-input`} />
              <button onClick={() => fileRef.current?.click()} disabled={busy}
                className="w-full py-8 rounded-xl border-2 border-dashed border-gray-300 hover:border-[#103B31] transition-colors flex flex-col items-center gap-2 text-[#64748B] disabled:opacity-50"
                data-testid={`${testId}-pick-file`}>
                {busy ? (
                  <div className="w-6 h-6 border-3 border-[#103B31] border-t-transparent rounded-full animate-spin" />
                ) : (
                  <FileText className="w-6 h-6 text-[#103B31]" />
                )}
                <span className="text-sm font-semibold">{busy ? "Procesando..." : "Seleccionar archivo CSV"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
